import { InfoCircleBigIcon } from '@app/components/icons/info-circle-big';
import { SortAscIcon } from '@app/components/icons/sort-ascending';
import { SortDescIcon } from '@app/components/icons/sort-descending';
import { Tooltip } from '@app/components/tooltip';
import {
  ADMIN_TOOLTIP_LOCATIONS,
  getAdminSortingOrder,
} from '@app/features/analytics/analytics-tracking';
import { Button } from '@app/features/analytics/components';
import { ErrorScreen } from '@app/features/analytics/components/error-states/error-screen';
import { useAnalytics } from '@app/features/analytics/hooks/use-analytics';
import { getSlugFromPath } from '@hopin-team/dashboard-shared';
import cx from 'classnames';
import React, { useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { AdminSkeleton } from '../admin-skeleton';
import {
  TOrganizerEventTypeDistribution,
  TOrganizerRow,
  TOrganizerSortKey,
} from '../hooks/use-events-by-organizer';
import { OrganizerRow } from './organizer-row';

export type TColumnHeaders = {
  name: string;
  email: string;
  status: string;
  seatAllocationDate: string;
  seatAllocationDateTooltip: string;
  recentEvent: string;
  recentEventTooltip: string;
  eventsHosted: string;
  eventsCreated: string;
  eventBreakdown: string;
};

export type TStatusLabels = {
  active: string;
  inviteSent: string;
  deleted: string;
  rejected: string;
};

type EventsByOrganizerCardProps = {
  data: TOrganizerRow[];
  title: string;
  tooltipContent: string;
  columnHeaders: TColumnHeaders;
  statusLabels: TStatusLabels;
  eventTypeLabels: Record<keyof TOrganizerEventTypeDistribution, string>;
  isError: boolean;
  inviteYourTeamLabel: string;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchMore: () => void;
  sortKey: TOrganizerSortKey;
  reverse: boolean;
  onSort: (key: TOrganizerSortKey) => void;
};

const TABLE_HEADER_CELL_CLASS =
  'sticky top-0 z-10 bg-white p-2 font-medium text-neutral-b1 text-sm text-left';

type SortableHeaderProps = {
  label: string;
  sortKey: TOrganizerSortKey;
  activeSortKey: TOrganizerSortKey;
  reverse: boolean;
  onSort: (key: TOrganizerSortKey, columnName: string) => void;
  tooltip?: string;
  tooltipTriggerTestId?: string;
};

const SortIndicator = ({
  isActive,
  reverse,
}: {
  isActive: boolean;
  reverse: boolean;
}) => (
  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center">
    {isActive && (reverse ? <SortDescIcon /> : <SortAscIcon />)}
  </span>
);

const SortableHeader = ({
  label,
  sortKey,
  activeSortKey,
  reverse,
  onSort,
  tooltip,
  tooltipTriggerTestId,
}: SortableHeaderProps) => {
  const isActive = sortKey === activeSortKey;

  const handleClick = () => {
    onSort(sortKey, label);
  };

  if (tooltip) {
    return (
      <div className="flex items-center gap-1">
        <button
          className="flex cursor-pointer items-center focus:outline-none"
          onClick={handleClick}
          type="button"
        >
          <span>{label}</span>
        </button>
        <Tooltip
          tooltip={tooltip}
          placement="bottom-center"
          className="inline-flex cursor-pointer"
          aria-label={tooltip}
          data-testid={tooltipTriggerTestId}
        >
          <InfoCircleBigIcon width={14} height={14} aria-hidden />
        </Tooltip>
        <button
          className="flex h-6 w-6 flex-shrink-0 cursor-pointer items-center justify-center focus:outline-none"
          onClick={handleClick}
          type="button"
          aria-label={`Sort ${label}`}
        >
          <SortIndicator isActive={isActive} reverse={reverse} />
        </button>
      </div>
    );
  }

  return (
    <button
      className="flex cursor-pointer items-center gap-1 focus:outline-none"
      onClick={handleClick}
      type="button"
    >
      <span>{label}</span>
      <SortIndicator isActive={isActive} reverse={reverse} />
    </button>
  );
};

export const FETCH_MORE_SKELETON_ROW_COUNT = 3;

export const EventsByOrganizerCard = ({
  data,
  title,
  tooltipContent,
  columnHeaders,
  statusLabels,
  eventTypeLabels,
  isError,
  inviteYourTeamLabel,
  hasNextPage,
  isFetchingNextPage,
  fetchMore,
  sortKey,
  reverse,
  onSort,
}: EventsByOrganizerCardProps) => {
  const { track } = useAnalytics();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const slug = getSlugFromPath(pathname);
  const isInviteYourTeamState = !isError && data.length <= 1;
  const shouldShowFetchMore =
    hasNextPage && !isFetchingNextPage && !isInviteYourTeamState && !isError;

  const handleTopEventCreatorsTooltipHover = useCallback(() => {
    track('ola_admin_tab_tooltip_hover', {
      location: ADMIN_TOOLTIP_LOCATIONS.topEventCreators,
    });
  }, [track]);

  const handleSortClick = useCallback(
    (key: TOrganizerSortKey, columnName: string) => {
      const nextReverse = key === sortKey ? !reverse : true;

      track('ola_admin_tab_sorting_click', {
        sorting_order: getAdminSortingOrder(nextReverse),
        column_name: columnName,
      });
      onSort(key);
    },
    [onSort, reverse, sortKey, track],
  );

  const handleFetchMore = useCallback(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchMore();
    }
  }, [hasNextPage, isFetchingNextPage, fetchMore]);

  const handleInviteYourTeam = useCallback(() => {
    navigate(`/org/${slug}/manage/team/active-members`);
  }, [navigate, slug]);

  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      track('ola_admin_tab_event_creators_scroll');

      if (!shouldShowFetchMore) {
        return;
      }

      const { scrollHeight, clientHeight, scrollTop } = event.currentTarget;
      if (scrollHeight <= clientHeight + 1) return;
      if (scrollTop < 1) return;
      if (scrollTop + clientHeight < scrollHeight - 5) return;

      handleFetchMore();
    },
    [handleFetchMore, shouldShowFetchMore, track],
  );

  return (
    <div
      className={cx(
        'overflow-hidden rounded-2xl border border-neutral-b4 bg-white p-6 flex flex-col gap-2',
        isInviteYourTeamState ? 'h-auto' : 'h-[35rem]',
      )}
      data-testid="events-by-organizer-card"
    >
      <div className="shrink-0 flex items-center justify-between">
        <span className="text-title text-neutral-b1">{title}</span>
        <Tooltip tooltip={tooltipContent} placement="bottom-center">
          <span
            className="cursor-pointer"
            aria-label={tooltipContent}
            data-testid="events-by-organizer-tooltip-trigger"
            onMouseEnter={handleTopEventCreatorsTooltipHover}
          >
            <InfoCircleBigIcon width={24} height={24} aria-hidden />
          </span>
        </Tooltip>
      </div>
      {isError ? (
        <div className="min-h-0 flex flex-1 items-center justify-center overflow-auto">
          <ErrorScreen showButton={false} />
        </div>
      ) : (
        <div
          className="min-h-0 flex-1 overflow-auto"
          data-testid="events-by-organizer-scroll-container"
          onScroll={handleScroll}
        >
          <table className="w-full table-fixed">
            <colgroup>
              <col className="w-[3%]" />
              <col className="w-[10%]" />
              <col className="w-[17%]" />
              <col className="w-[6%]" />
              <col className="w-[15%]" />
              <col className="w-[11%]" />
              <col className="w-[11%]" />
              <col className="w-[12%]" />
              <col />
            </colgroup>

            <thead>
              <tr className="border-b border-neutral-b4">
                <th className={TABLE_HEADER_CELL_CLASS} />
                <th className={TABLE_HEADER_CELL_CLASS}>
                  <SortableHeader
                    label={columnHeaders.name}
                    sortKey="name"
                    activeSortKey={sortKey}
                    reverse={reverse}
                    onSort={handleSortClick}
                  />
                </th>
                <th className={TABLE_HEADER_CELL_CLASS}>
                  <SortableHeader
                    label={columnHeaders.email}
                    sortKey="email"
                    activeSortKey={sortKey}
                    reverse={reverse}
                    onSort={handleSortClick}
                  />
                </th>
                <th className={TABLE_HEADER_CELL_CLASS}>
                  <SortableHeader
                    label={columnHeaders.status}
                    sortKey="status"
                    activeSortKey={sortKey}
                    reverse={reverse}
                    onSort={handleSortClick}
                  />
                </th>
                <th className={TABLE_HEADER_CELL_CLASS}>
                  <SortableHeader
                    label={columnHeaders.seatAllocationDate}
                    sortKey="seat_allocation_date"
                    activeSortKey={sortKey}
                    reverse={reverse}
                    onSort={handleSortClick}
                    tooltip={columnHeaders.seatAllocationDateTooltip}
                    tooltipTriggerTestId="seat-allocation-date-tooltip-trigger"
                  />
                </th>
                <th className={TABLE_HEADER_CELL_CLASS}>
                  <SortableHeader
                    label={columnHeaders.eventsCreated}
                    sortKey="events_created"
                    activeSortKey={sortKey}
                    reverse={reverse}
                    onSort={handleSortClick}
                  />
                </th>
                <th className={TABLE_HEADER_CELL_CLASS}>
                  <SortableHeader
                    label={columnHeaders.eventsHosted}
                    sortKey="events_hosted"
                    activeSortKey={sortKey}
                    reverse={reverse}
                    onSort={handleSortClick}
                  />
                </th>
                <th className={TABLE_HEADER_CELL_CLASS}>
                  <SortableHeader
                    label={columnHeaders.recentEvent}
                    sortKey="recent_event_date"
                    activeSortKey={sortKey}
                    reverse={reverse}
                    onSort={handleSortClick}
                    tooltip={columnHeaders.recentEventTooltip}
                    tooltipTriggerTestId="recent-event-tooltip-trigger"
                  />
                </th>
                <th className={TABLE_HEADER_CELL_CLASS}>
                  {columnHeaders.eventBreakdown}
                </th>
              </tr>
            </thead>

            <tbody>
              {data.map(organizer => (
                <OrganizerRow
                  key={organizer.id}
                  name={organizer.name}
                  email={organizer.email}
                  pictureUrl={organizer.pictureUrl}
                  status={organizer.status}
                  statusLabel={statusLabels[organizer.status]}
                  seatAllocationDate={organizer.seatAllocationDate}
                  recentEventDate={organizer.recentEventDate}
                  eventsHosted={organizer.eventsHosted}
                  eventsCreated={organizer.eventsCreated}
                  eventTypeDistribution={organizer.eventTypeDistribution}
                  eventTypeLabels={eventTypeLabels}
                  rowTestId="organizer-row"
                  statusTestId="organizer-status"
                />
              ))}
              {isInviteYourTeamState && (
                <tr>
                  <td className="px-2 pt-3" colSpan={9}>
                    <div className="flex justify-center">
                      <Button
                        className="!rounded-lg border-neutral-b4"
                        data-testid="invite-your-team-button"
                        inline
                        kind="secondary"
                        onClick={handleInviteYourTeam}
                        pattern="outline"
                        size="medium"
                      >
                        {inviteYourTeamLabel}
                      </Button>
                    </div>
                  </td>
                </tr>
              )}
              {isFetchingNextPage && !isInviteYourTeamState && (
                <AdminSkeleton
                  rowCount={FETCH_MORE_SKELETON_ROW_COUNT}
                  variant="rows"
                />
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
